import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import { withBreakpoints } from 'react-breakpoints';
import { selectGroupByField, selectFilters } from 'store/Filters/selectors';
import { selectCustomAttributes } from 'store/CustomAttributes/selectors';
import { selectVulnsGeneralFields } from 'store/Preferences/vulnsGeneral/selectors';
import { setFieldWidth } from 'store/Preferences/vulnsGeneral/actions';
import { PropTypes } from 'prop-types';
import SharedTable from 'shared/ui/Table';
import vulnsGeneralSchema from './schema';
import { handleResizedChange, getThProps, getTrProps, findSelectedVuln } from 'Screens/Contextualization/Manage/components/ManageTable/components/Table/Functions';

import {
  selectIsFetching, selectIsFetchingExpandedData, selectShowContextMenu, selectShowDetail,
  selectVulns, selectVulnsSelected
} from 'store/ManageGeneral/selectors';
import {
  expandGroupByVulns, selectVulnRow, setOrderByVulnsGeneral,
  setShowContextMenu, unSelectAllVulns
} from 'store/ManageGeneral/actions';
import SubTable from './components/SubTable';
import { selectRiskScoreFeatureFlag } from 'store/Faraday/selectors';
import useTheme from 'Hooks/UseTheme';
import { selectShowQuickPlanner } from 'store/Manage/selectors';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_TAGS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';

const Table = ({ currentBreakpoint }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState([]);
  const groupByField = useSelector((state) => selectGroupByField('vulnsGeneral', state));
  const customFields = useSelector(selectCustomAttributes);
  const showVulnDetail = useSelector(selectShowDetail);
  const vulnsSelected = useSelector(selectVulnsSelected);
  const isFetching = useSelector(selectIsFetching);
  const isFetchingExpandedData = useSelector(selectIsFetchingExpandedData);
  const data = useSelector(selectVulns);
  const fields = useSelector(selectVulnsGeneralFields);
  const filters = useSelector((state) => selectFilters('vulnsGeneral', state));
  const isAllowedByLicense = useSelector(selectRiskScoreFeatureFlag);
  const isTagsAllowedByLicense = useSelector((state) => selectAllowedByRole(state, FEATURE_TAGS, PERMISSION_TYPES.READ));
  const showContextMenu = useSelector(selectShowContextMenu);
  const noDataText = isFetching ? '' : <FormattedMessage id="workspaces.table.noData" />;
  const showQuickPlanner = useSelector(selectShowQuickPlanner);
  const { themeStyles } = useTheme();
  const context = {
    isPivot: true,
    customFields,
    showVulnDetail,
    currentBreakpoint,
    groupBy: groupByField,
    fields,
    isFetching,
    isFetchingExpandedData,
    hasGroupedData: false,
    isAllowedByLicense,
    isTagsAllowedByLicense,
    showQuickPlanner,
    theme: themeStyles
  };
  const selectRowAction = (row) => dispatch(selectVulnRow({}, row));
  const unSelectAllAction = (row) => dispatch(unSelectAllVulns({}, row));
  const setFieldWidthAction = (field, value) => dispatch(setFieldWidth(field, value, 'manage_table'));
  const showContextMenuAction = (value, xPos, yPos) => dispatch(setShowContextMenu(value, xPos, yPos));

  const onExpandedChange = (expanded, index) => {
    if (expanded[index]) {
      const expandData = data[index];
      dispatch(expandGroupByVulns(index, expandData));
    }
    setExpanded(expanded);
  };

  const handleResizeColumn = (newResized) => !groupByField && handleResizedChange(newResized, fields, setFieldWidthAction);

  const fetchData = (sorted) => !isFetching && dispatch(setOrderByVulnsGeneral(sorted));

  useEffect(() => {
    setExpanded([]);
  }, [groupByField, filters]);

  return (
    <SharedTable
      schema={ vulnsGeneralSchema }
      context={ context }
      data={ data }
      isLoading={ isFetching && !groupByField }
      onFetch={ fetchData }
      getRowProps={ (rowInfo) => getTrProps(rowInfo, vulnsSelected, selectRowAction, unSelectAllAction, showContextMenuAction, isFetching) }
      isRowSelected={ (rowInfo) => !!findSelectedVuln(rowInfo, vulnsSelected) }
      getHeaderProps={ (column) => getThProps(column, fields, currentBreakpoint, showVulnDetail, showContextMenuAction, showContextMenu) }
      onResize={ debounce(handleResizeColumn, 200) }
      expanded={ expanded }
      onExpandedChange={ onExpandedChange }
      noDataText={ noDataText }
      SubComponent={ (row) => <SubTable row={ row } context={ context } /> }
    />
  );
};

Table.propTypes = {
  currentBreakpoint: PropTypes.string.isRequired
};

export default withBreakpoints(Table);
